import React, { useCallback } from "react"
import { motion } from "framer-motion"
import ClockImg from "../../../../../assets/avia/sidebar/clock.png"
import { FLIGHT_DURATION_VARIABLES } from "../../utils/variables/search"

const Duration = ({ duration, title, type = FLIGHT_DURATION_VARIABLES.PARAM_KEY, onChange, t }) => {
  const { min, max } = duration
  const { DEFAULT_MIN, DEFAULT_MAX } = FLIGHT_DURATION_VARIABLES

  const handleLeftChange = useCallback(
    (e) => {
      const newLeft = Math.min(Number(e.target.value), max - 1)
      onChange({ key: `${type}.min`, value: newLeft, overwrite: true })
    },
    [max, onChange, type]
  )

  const handleRightChange = useCallback(
    (e) => {
      const newRight = Math.max(Number(e.target.value), min + 1)
      onChange({ key: `${type}.max`, value: newRight, overwrite: true })
    },
    [min, onChange, type]
  )

  const leftPercent = ((min - DEFAULT_MIN) / (DEFAULT_MAX - DEFAULT_MIN)) * 100
  const rightPercent = ((max - DEFAULT_MIN) / (DEFAULT_MAX - DEFAULT_MIN)) * 100

  return (
    <motion.div
      className="mb-6 flex flex-col gap-1 relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="">
        <span className="text-dark-gray font-medium text-lg">{title}</span>
      </div>

      <div className="flex flex-col">
        <div className="flex justify-between w-full">
          <DurationLabel value={min} t={t} />
          <DurationLabel value={max} t={t} />
        </div>
        <div className="px-4">
          <div className="relative h-6">
            {/* Background track */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 bg-gray-300 rounded-full" />

            {/* Active range */}
            <motion.div
              className="absolute top-1/2 -translate-y-1/2 h-1 bg-dark-gray rounded-full"
              animate={{
                left: `${leftPercent}%`,
                right: `${100 - rightPercent}%`,
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />

            {/* Left handle */}
            <motion.div
              className="absolute top-1/2 -translate-y-1/2 w-[18px] h-[18px] bg-white rounded-full shadow flex items-center justify-center cursor-pointer z-10"
              animate={{ left: `calc(${leftPercent}% - 10px)` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              // whileHover={{ scale: 1.2 }}
              // whileTap={{ scale: 0.95 }}
              onMouseDown={(e) => {
                e.preventDefault()
                const startX = e.clientX
                const startValue = min
                const container = e.currentTarget.parentElement
                const rect = container.getBoundingClientRect()

                const handleMouseMove = (moveEvent) => {
                  const deltaX = moveEvent.clientX - startX
                  const deltaPercent = (deltaX / rect.width) * 100
                  const valueRange = DEFAULT_MAX - DEFAULT_MIN
                  const deltaValue = Math.round((deltaPercent / 100) * valueRange)
                  const newValue = Math.max(DEFAULT_MIN, Math.min(max - 1, startValue + deltaValue))

                  handleLeftChange({ target: { value: newValue } })
                }

                const handleMouseUp = () => {
                  document.removeEventListener("mousemove", handleMouseMove)
                  document.removeEventListener("mouseup", handleMouseUp)
                }

                document.addEventListener("mousemove", handleMouseMove)
                document.addEventListener("mouseup", handleMouseUp)
              }}
            >
              <img src={ClockImg} alt="clock" className="w-full h-full pointer-events-none" />
            </motion.div>

            <motion.div
              className="absolute top-1/2 -translate-y-1/2 w-[18px] h-[18px] bg-white rounded-full shadow flex items-center justify-center cursor-pointer z-10"
              animate={{ left: `calc(${rightPercent}% - 10px)` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              // whileHover={{ scale: 1.2 }}
              // whileTap={{ scale: 0.95 }}
              onMouseDown={(e) => {
                e.preventDefault()
                const startX = e.clientX
                const startValue = max
                const container = e.currentTarget.parentElement
                const rect = container.getBoundingClientRect()

                const handleMouseMove = (moveEvent) => {
                  const deltaX = moveEvent.clientX - startX
                  const deltaPercent = (deltaX / rect.width) * 100
                  const valueRange = DEFAULT_MAX - DEFAULT_MIN
                  const deltaValue = Math.round((deltaPercent / 100) * valueRange)
                  const newValue = Math.max(min + 1, Math.min(DEFAULT_MAX, startValue + deltaValue))

                  handleRightChange({ target: { value: newValue } })
                }

                const handleMouseUp = () => {
                  document.removeEventListener("mousemove", handleMouseMove)
                  document.removeEventListener("mouseup", handleMouseUp)
                }

                document.addEventListener("mousemove", handleMouseMove)
                document.addEventListener("mouseup", handleMouseUp)
              }}
            >
              <img src={ClockImg} alt="clock" className="w-full h-full pointer-events-none" />
            </motion.div>

            {/* Hidden range inputs */}
            <input
              type="range"
              min={DEFAULT_MIN}
              max={DEFAULT_MAX}
              value={min}
              onChange={handleLeftChange}
              className="absolute top-0 left-0 w-full h-6 bg-transparent appearance-none cursor-pointer pointer-events-auto"
              style={{
                zIndex: min > DEFAULT_MAX - (DEFAULT_MAX - DEFAULT_MIN) / 2 ? 5 : 3,
                opacity: 0,
              }}
            />

            <input
              type="range"
              min={DEFAULT_MIN}
              max={DEFAULT_MAX}
              value={max}
              onChange={handleRightChange}
              className="absolute top-0 left-0 w-full h-6 bg-transparent appearance-none cursor-pointer pointer-events-auto"
              style={{
                zIndex: min > DEFAULT_MAX - (DEFAULT_MAX - DEFAULT_MIN) / 2 ? 3 : 5,
                opacity: 0,
              }}
            />
          </div>
        </div>
      </div>
      <div className="absolute -bottom-3 left-0 right-0 border-b border-light-gray/25"></div>
    </motion.div>
  )
}

export default Duration

const DurationLabel = ({ value, t }) => {
  let hours

  if (value === 1) {
    hours = t("admin.cont_hour_1")
  } else if (value < 5) {
    hours = t("admin.cont_hour_2")
  } else {
    hours = t("admin.cont_hour_3")
  }

  return (
    <motion.span
      className="text-dark-gray text-[13px] px-1 rounded-md"
      key={value}
      initial={{ scale: 1 }}
      animate={{ scale: [1, 1.1, 1] }}
      transition={{ duration: 0.2 }}
    >
      {value} {hours.toLowerCase()}
    </motion.span>
  )
}
